'use client';

import React, {useMemo} from 'react';

import {useFeatures} from '@udemy-v2/react-experimentation';

import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {Carousel, CarouselSlide, CarouselSlideLayout} from '@udemy-v2/react-carousel-components';
import {ShowMore} from '@udemy-v2/react-reveal-components';

import {MWEB_SEARCH_HEADER_OPTIMIZATION} from '@/app/lib/experimentation';
import {Heading} from '@/lib/heading-level';
import {useIsClient} from '@/lib/use-is-client';
import {useMarketingSplitCardsCarouselV1Query} from '@/generated/graphql';

import {useCmsPreviewContent} from '../../data/hooks/use-cms-preview-content';
import {adaptEnrichedSkillsData} from '../../data/parse';
import type {EnrichedSkillsData} from '../../data/types';
import {CardLink} from './card-link.client';
import {EnrichedSkillsControlsSkeleton} from './enriched-skills-controls-skeleton';
import styles from './enriched-skills.module.scss';
import {useContentArea, withinOrWiderThan} from '@udemy-v2/react-layout-components';
import {stripHtml} from '@/lib/strip-html';

const useIsOptimizedSearchHeaderMWeb = (): boolean => {
    const {featureVariantAssignments} = useFeatures([MWEB_SEARCH_HEADER_OPTIMIZATION]);
    return Boolean(
        featureVariantAssignments.get(MWEB_SEARCH_HEADER_OPTIMIZATION)?.configuration
            ?.showOptimizedSearchHeaderMWeb,
    );
};

type EnrichedSkillsClientProps = {
    initialData: EnrichedSkillsData;
    headingId: string;
};

const useEnrichedSkillsSlideLayout = (): CarouselSlideLayout => {
    const {range, isRangeKnown} = useContentArea();

    const slidesToScroll = isRangeKnown && withinOrWiderThan(range, 'xl') ? 3 : 2;

    return useMemo(() => {
        return {containerClassName: styles['slide-layout'], slidesToScroll};
    }, [slidesToScroll]);
};

export const EnrichedSkillsClient: React.FC<EnrichedSkillsClientProps> = ({
    initialData,
    headingId,
}) => {
    const data = useCmsPreviewContent(initialData, {
        fetcher: useMarketingSplitCardsCarouselV1Query.fetcher,
        variables: {},
        adapt: adaptEnrichedSkillsData,
    });

    const isMobileMax = useMatchMedia('xs-max');
    const slideLayout = useEnrichedSkillsSlideLayout();
    const isClient = useIsClient();
    const isOptimizedSearchHeaderMWeb = useIsOptimizedSearchHeaderMWeb();

    const sectionClassName = isOptimizedSearchHeaderMWeb
        ? `${styles['enriched-skills']} ${styles['enriched-skills--mweb-optimized-search']}`
        : styles['enriched-skills'];

    const mobileCards = useMemo(
        () =>
            data.cards.map((card, index) => (
                <CardLink key={card.title} card={card} index={index} />
            )),
        [data.cards],
    );
    const cardAriaLabels = useMemo(() => data.cards.map((card) => card.title), [data.cards]);

    return (
        <section
            className={sectionClassName}
            data-purpose="enriched-skills"
            role="region"
            aria-labelledby={headingId}
        >
            <div className={styles['inner-grid']}>
                <div className={styles['heading-area']}>
                    <Heading
                        id={headingId}
                        className={`${styles.headline} ud-heading-xxl`}
                        dangerouslySetInnerHTML={{__html: data.header.headline}}
                    />
                    {data.header.supportingText && (
                        <p className={styles['supporting-text']}>{data.header.supportingText}</p>
                    )}
                </div>
                <div className={styles['cards-area']}>
                    {isMobileMax ? (
                        <ShowMore collapsedHeight={824}>
                            <div className={styles['mobile-card-grid']}>{mobileCards}</div>
                        </ShowMore>
                    ) : (
                        <>
                            <Carousel
                                ariaLabel={stripHtml(data.header.headline)}
                                controlLayout="pagination-below-with-dots"
                                slideLayout={slideLayout}
                                slides={mobileCards.map((slideEl, index) => (
                                    <CarouselSlide
                                        key={cardAriaLabels[index]}
                                        aria-label={cardAriaLabels[index]}
                                    >
                                        {slideEl}
                                    </CarouselSlide>
                                ))}
                            />
                            {!isClient && <EnrichedSkillsControlsSkeleton />}
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};
